/**
 * A per-user synced document with a local cache (SPEC §1A.3): the pattern `BracketStore`
 * (brackets/sync.ts) introduced, made generic so other field-merge documents (the watchlists,
 * `watchlist/main`) reuse it instead of copying it.
 *
 * - The document is cached in local storage for an instant start (and offline edits); a
 *   `:sync` entry beside it keeps the synced version and a dirty flag.
 * - `start()` loads once per window: a newer server copy wins over a clean cache; local edits
 *   (or the seed, on a fresh account) are pushed.
 * - `update()` applies a pure change, persists locally, notifies, and saves after a debounce;
 *   concurrent saves share one request. On a 409 `DocSync` field-merges and retries once.
 * - `refresh(version)` adopts another device's save (a `DocChanged` frame).
 *
 * Storage and the document client are injected so the round trip is unit-tested; the dev preview
 * runs with `sync: false` and its own storage key.
 */
import { DocSync } from "./client.ts";
import type { DocType } from "./docs.ts";
import type { KeyValueStorage } from "../workspacesSync.ts";

type SyncMeta = { version: number; dirty: boolean };

const sameJson = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

function parseJson(s: string | null): unknown {
  if (s === null) return undefined;
  try {
    return JSON.parse(s) as unknown;
  } catch {
    return undefined;
  }
}

export type SyncedDocOptions<T> = {
  docType: DocType;
  docId: string;
  storageKey: string;
  storage: KeyValueStorage;
  sync: boolean;
  /** Validate / migrate an untrusted body; null when unusable. */
  sanitize: (raw: unknown) => T | null;
  /** First-run content (not saved until `start()` finds no server copy). */
  seed: () => T;
  docs?: DocSync;
  saveDelayMs?: number;
};

export class SyncedDocStore<T extends Record<string, unknown>> {
  private snap: T;
  private readonly listeners = new Set<() => void>();
  private readonly o: Required<Omit<SyncedDocOptions<T>, "docs" | "saveDelayMs">>;
  private readonly docs: DocSync;
  private readonly saveDelayMs: number;
  private meta: SyncMeta;
  /** Server body at `meta.version` (the If-Match base for the field merge). */
  private base: T | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private saving: Promise<void> | null = null;
  private started = false;

  constructor(opts: SyncedDocOptions<T>) {
    this.o = {
      docType: opts.docType,
      docId: opts.docId,
      storageKey: opts.storageKey,
      storage: opts.storage,
      sync: opts.sync,
      sanitize: opts.sanitize,
      seed: opts.seed,
    };
    this.docs = opts.docs ?? new DocSync();
    this.saveDelayMs = opts.saveDelayMs ?? 800;
    const stored = opts.sanitize(parseJson(opts.storage.get(opts.storageKey)));
    const meta = parseJson(opts.storage.get(`${opts.storageKey}:sync`)) as Partial<SyncMeta> | undefined;
    this.meta = {
      version: typeof meta?.version === "number" ? meta.version : 0,
      dirty: meta?.dirty === true,
    };
    this.snap = stored ?? opts.seed();
  }

  // ---- subscription (React: useSyncExternalStore) -------------------------------------------

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };

  getSnapshot = (): T => this.snap;

  get doc(): T {
    return this.snap;
  }

  get syncedVersion(): number {
    return this.meta.version;
  }

  get dirty(): boolean {
    return this.meta.dirty;
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  // ---- mutations ---------------------------------------------------------------------------

  /** Apply a pure change; no-op when it returns the same document. */
  update(change: (d: T) => T): void {
    const next = change(this.snap);
    if (next === this.snap) return;
    this.snap = next;
    this.meta = { ...this.meta, dirty: true };
    this.persistLocal();
    this.emit();
    this.scheduleSave();
  }

  private persistLocal(): void {
    this.o.storage.set(this.o.storageKey, JSON.stringify(this.snap));
    this.o.storage.set(`${this.o.storageKey}:sync`, JSON.stringify(this.meta));
  }

  // ---- server sync -------------------------------------------------------------------------

  async start(): Promise<void> {
    if (!this.o.sync || this.started) return;
    this.started = true;
    try {
      const server = await this.docs.load<Record<string, unknown>>(this.o.docType, this.o.docId);
      if (server) {
        const body = this.o.sanitize(server.body);
        if (body && (server.version > this.meta.version || !this.meta.dirty)) {
          this.adopt(body, server.version);
          return;
        }
        this.base = body;
        this.meta = { ...this.meta, version: server.version };
      } else {
        this.meta = { version: 0, dirty: true };
      }
      if (this.meta.dirty) await this.save();
    } catch {
      // API unreachable: keep the local copy; the next change retries.
    }
  }

  async refresh(version: number): Promise<void> {
    if (!this.o.sync || version <= this.meta.version) return;
    try {
      const server = await this.docs.load<Record<string, unknown>>(this.o.docType, this.o.docId);
      if (!server || server.version <= this.meta.version) return;
      const body = this.o.sanitize(server.body);
      if (body) this.adopt(body, server.version);
    } catch {
      /* the next change or reload catches up */
    }
  }

  private adopt(body: T, version: number): void {
    this.base = body;
    this.meta = { version, dirty: false };
    if (!sameJson(this.snap, body)) {
      this.snap = body;
      this.emit();
    }
    this.persistLocal();
  }

  private scheduleSave(): void {
    if (!this.o.sync || !this.started) return;
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.save();
    }, this.saveDelayMs);
    (this.saveTimer as { unref?: () => void }).unref?.();
  }

  async save(): Promise<void> {
    if (!this.o.sync) return;
    if (this.saving) {
      await this.saving;
      if (this.meta.dirty) await this.save();
      return;
    }
    this.saving = this.saveOnce();
    try {
      await this.saving;
    } finally {
      this.saving = null;
    }
  }

  private async saveOnce(): Promise<void> {
    const local = this.snap;
    try {
      const r = await this.docs.save(
        this.o.docType,
        this.o.docId,
        this.base ?? local,
        this.meta.version,
        local,
      );
      const server = r.kind === "saved" ? r.doc : r.server;
      const body = this.o.sanitize(server.body);
      if (r.kind === "saved" && this.snap !== local) {
        this.base = body ?? local;
        this.meta = { version: server.version, dirty: true };
        this.persistLocal();
        this.scheduleSave();
        return;
      }
      if (body) this.adopt(body, server.version);
    } catch {
      // Offline: stays dirty; retried on the next change or reload.
    }
  }

  /** Flush a pending debounced save (page hide). */
  flush(): void {
    if (this.saveTimer === null) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
    void this.save();
  }
}
